<template>
  <div class="root-container">
    <div class="table-container">
      <!-- 搜索栏 -->
      <div class="search-container" :class="{ 'mobile-search-top': !isDesktop }">
        <!-- 电脑端 -->
        <div v-if="isDesktop" class="desktop-search-wrapper">
          <div class="search-row">
            <el-input
              v-model="searchKeyword"
              placeholder="搜索IP/页面/浏览器/系统"
              prefix-icon="el-icon-search"
              class="search-input"
              style="max-width:320px;"
              @keyup.enter="handleSearch"
            >
              <template slot="append">
                <el-button @click="handleSearch">搜索</el-button>
              </template>
            </el-input>

            <div class="filter-group">
              <el-select v-model="filterForm.language" placeholder="语言" style="width:100px;" clearable @change="applyFilter">
                <el-option value="" label="全部语言" />
                <el-option v-for="lang in languageList" :key="lang.code" :label="lang.name" :value="lang.code" />
              </el-select>
              <el-select v-model="filterForm.device" placeholder="设备" style="width:110px;" clearable @change="applyFilter">
                <el-option value="" label="全部设备" />
                <el-option value="desktop" label="桌面端" />
                <el-option value="mobile" label="手机" />
                <el-option value="tablet" label="平板" />
              </el-select>
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd"
                style="width:260px;"
                @change="handleDateChange"
              />
            </div>
          </div>
        </div>

        <!-- 手机端 -->
        <div v-else class="mobile-search-wrapper">
          <div class="mobile-filter-row">
            <el-select v-model="filterForm.language" placeholder="语言" size="small" style="width:90px;" clearable @change="applyFilter">
              <el-option value="" label="全部" />
              <el-option v-for="lang in languageList" :key="lang.code" :label="lang.name" :value="lang.code" />
            </el-select>
            <el-select v-model="filterForm.device" placeholder="设备" size="small" style="width:90px;" clearable @change="applyFilter">
              <el-option value="" label="全部" />
              <el-option value="desktop" label="桌面" />
              <el-option value="mobile" label="手机" />
              <el-option value="tablet" label="平板" />
            </el-select>
          </div>
          <div class="mobile-search-row">
            <van-search
              v-model="searchKeyword"
              placeholder="搜索IP/页面/浏览器"
              shape="round"
              @search="handleSearch"
            />
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="content-container">
        <!-- 电脑端表格 -->
        <div class="table-wrapper" v-if="isDesktop">
          <el-table :data="tableData" style="width:100%" tooltip-effect="dark" empty-text="暂无数据"
            class="desktop-table" :height="tableHeight">
            <el-table-column label="IP" prop="ip" align="left" width="140px" fixed>
              <template slot-scope="scope">
                <span style="font-family:monospace;font-size:13px;">{{ scope.row.ip || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="地区" align="left" width="150px">
              <template slot-scope="scope">
                <span v-if="scope.row.country">{{ scope.row.country }}</span>
                <span v-else style="color:#909399;">--</span>
                <span v-if="scope.row.prov" style="color:#909399;"> / {{ scope.row.prov }}</span>
                <span v-if="scope.row.city" style="color:#909399;"> {{ scope.row.city }}</span>
              </template>
            </el-table-column>
            <el-table-column label="访问页面" align="left" width="200px">
              <template slot-scope="scope">
                <div style="font-size:13px;">{{ scope.row.page_url || '--' }}</div>
                <div v-if="scope.row.page_title" style="font-size:11px;color:#909399;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:180px;">{{ scope.row.page_title }}</div>
              </template>
            </el-table-column>
            <el-table-column label="浏览器" align="left" width="130px">
              <template slot-scope="scope">
                <span>{{ scope.row.browser_name || '--' }}</span>
                <span v-if="scope.row.browser" style="font-size:11px;color:#909399;margin-left:4px;">{{ scope.row.browser.replace(scope.row.browser_name, '') }}</span>
              </template>
            </el-table-column>
            <el-table-column label="系统" prop="os" align="left" width="110px">
              <template slot-scope="scope">
                <span>{{ scope.row.os || '--' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="设备" align="center" width="80px">
              <template slot-scope="scope">
                <el-tag :type="scope.row.device === 'mobile' ? 'warning' : scope.row.device === 'tablet' ? 'info' : ''" size="mini">
                  {{ deviceMap[scope.row.device] || scope.row.device || '--' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="语言" prop="language" align="center" width="70px">
              <template slot-scope="scope">
                <el-tag size="mini" :type="scope.row.language === 'en' ? 'success' : ''">{{ scope.row.language || '--' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="来源" align="left">
              <template slot-scope="scope">
                <span v-if="scope.row.referrer" style="font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:block;max-width:120px;" :title="scope.row.referrer">{{ scope.row.referrer }}</span>
                <span v-else style="color:#909399;">直接访问</span>
              </template>
            </el-table-column>
            <el-table-column label="访问时间" prop="created_at" align="left" width="170px" fixed="right">
            </el-table-column>
          </el-table>
        </div>

        <!-- 手机端卡片 -->
        <div v-else class="mobile-cards">
          <div v-if="tableData.length === 0" class="mobile-empty">暂无数据</div>
          <div v-for="item in tableData" :key="item.id" class="mobile-card">
            <div class="card-header">
              <div class="card-title">
                <h3>{{ item.ip || '--' }}</h3>
                <div class="card-meta">
                  <el-tag :type="item.device === 'mobile' ? 'warning' : item.device === 'tablet' ? 'info' : ''" size="mini">{{ deviceMap[item.device] || item.device }}</el-tag>
                  <el-tag size="mini" :type="item.language === 'en' ? 'success' : ''">{{ item.language || '--' }}</el-tag>
                </div>
              </div>
              <span style="font-size:11px;color:#909399;">{{ item.created_at }}</span>
            </div>
            <div class="card-content">
              <p><strong>页面：</strong><span style="font-family:monospace;">{{ item.page_url || '--' }}</span></p>
              <p v-if="item.page_title"><strong>标题：</strong>{{ item.page_title }}</p>
              <p><strong>地区：</strong>{{ item.country || '--' }} {{ item.prov ? '/' + item.prov : '' }} {{ item.city || '' }}</p>
              <p><strong>浏览器：</strong>{{ item.browser || '--' }}</p>
              <p><strong>系统：</strong>{{ item.os || '--' }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="isDesktop && total > 0" class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :current-page.sync="params.page"
          :page-sizes="[20, 50, 100]"
          :page-size.sync="params.page_count"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
      <div v-else-if="!isDesktop && total > 0" class="mobile-pagination">
        <van-pagination
          v-model="params.page"
          :total-items="total"
          :items-per-page="params.page_count"
          :show-page-size="3"
          force-ellipses
          @change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getVisitLogs } from '@/api/website'
import { getLanguage } from '@/api/public'

export default {
  name: 'VisitLogsPage',
  data() {
    return {
      isDesktop: true,
      searchKeyword: '',
      dateRange: null,
      filterForm: {
        language: '',
        device: ''
      },
      languageList: [],
      tableData: [],
      params: {
        page: 1,
        page_count: 20
      },
      total: 0,
      deviceMap: {
        desktop: '桌面端',
        mobile: '手机',
        tablet: '平板'
      }
    }
  },
  computed: {
    tableHeight() {
      if (!this.isDesktop) return 'auto'
      // 计算表格高度：减去顶部栏、筛选区域、分页栏和底部的高度
      const headerHeight = 60 // 顶部栏高度
      const searchHeight = this.isFilterOpen ? 200 : 60 // 搜索栏高度（包含筛选面板）
      const paginationHeight = 60 // 分页栏高度
      const footerHeight = 40 // 底部高度
      const padding = 20 // 页面内边距
      return window.innerHeight - headerHeight - searchHeight - paginationHeight - footerHeight - padding - 120 // 100是其他元素的额外高度
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.handleResize)
    this.getLanguageList()
    this.getTableData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.checkScreenSize()
    },
    checkScreenSize() {
      this.isDesktop = window.innerWidth > 768
    },
    getLanguageList() {
      var vm = this
      getLanguage().then(function(res) {
        vm.languageList = res.data || []
      }).catch(function() {})
    },
    handleSearch() {
      this.params.page = 1
      this.getTableData()
    },
    handleDateChange(val) {
      if (val && val.length === 2) {
        this.filterForm.date_start = val[0]
        this.filterForm.date_end = val[1]
      } else {
        this.filterForm.date_start = ''
        this.filterForm.date_end = ''
      }
      this.applyFilter()
    },
    applyFilter() {
      this.params.page = 1
      this.getTableData()
    },
    getTableData() {
      var vm = this
      var params = {
        page: vm.params.page,
        page_count: vm.params.page_count,
        keyword: vm.searchKeyword,
        language: vm.filterForm.language,
        device: vm.filterForm.device,
        date_start: vm.filterForm.date_start || '',
        date_end: vm.filterForm.date_end || ''
      }
      getVisitLogs(params).then(function(res) {
        if (res.code === 200) {
          var data = res.data || {}
          vm.tableData = data.data || []
          vm.total = data.total || 0
        }
      }).catch(function() {})
    },
    handleSizeChange(val) {
      this.params.page_count = val
      this.params.page = 1
      this.getTableData()
    },
    handleCurrentChange(val) {
      this.params.page = val
      this.getTableData()
    }
  }
}
</script>

<style scoped lang="scss">
.table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-container {
  margin-bottom: 0;
  padding: 20px;
  background-color: var(--background);
  border-radius: var(--border-radius);
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.mobile-search-top {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;
  padding: 10px 15px;
}

.desktop-search-wrapper {
  width: 100%;
}

.search-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-input {
  width: 100%;
  max-width: 400px;
  font-size: var(--font-size-sm);
}

.mobile-search-wrapper {
  width: 100%;
}

.mobile-filter-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.mobile-search-row {
  margin-bottom: 8px;
}

/* 内容区域 */
.content-container {
  flex: 1;
  height: 100%;
  overflow: hidden;
  padding: 20px 0;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
}

.table-wrapper {
  height: 100%;
  margin-bottom: 0;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--shadow);
  font-size: 14px;
}

.desktop-table {
  height: 100%;
  margin-bottom: 0;
  font-size: 14px;
}

/* 手机端卡片 */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 0;
}

.mobile-card {
  border: 1px solid var(--border);
  border-radius: var(--border-radius-lg);
  padding: 15px;
  background-color: var(--bg-200);
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.mobile-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-light);
}

.card-title {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  margin-right: 10px;
}

.card-title h3 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text);
  font-family: monospace;
}

.card-meta {
  display: flex;
  gap: 6px;
}

.card-content {
  border-bottom: 1px solid var(--border-light);
  padding-bottom: 12px;
  margin-bottom: 0;
}

.card-content p {
  margin: 6px 0;
  font-size: var(--font-size-sm);
  color: var(--text-200);
  line-height: 1.5;
}

.card-content p strong {
  color: var(--text);
  font-weight: 500;
}

.mobile-empty {
  text-align: center;
  padding: 40px 0;
  color: var(--text-200);
  font-size: var(--font-size-sm);
}

/* 分页 */
.pagination-container {
  margin-top: 0;
  margin-bottom: 0;
  padding: 10px;
  border-top: 1px solid var(--border);
  text-align: right;
  box-sizing: border-box;
}

.mobile-pagination {
  padding: 15px;
  text-align: center;
}

/* 响应式 */
@media (max-width: 768px) {
  .search-container {
    width: 100vw;
    margin-left: -15px;
    border-bottom-left-radius: var(--border-radius);
    border-bottom-right-radius: var(--border-radius);
    background-color: var(--bg-200);
  }

  .search-container .search-input {
    display: none;
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: flex;
  }

  .content-container {
    padding: 10px 0;
  }

  .pagination-container {
    text-align: center;
    padding: 15px;
  }
}

@media (min-width: 769px) {
  .desktop-table {
    display: block;
  }

  .mobile-cards {
    display: none;
  }

  .mobile-search-wrapper,
  .mobile-pagination {
    display: none;
  }
}
</style>
